import {
  FHIREngineRenderer,
  PartialSave
} from '@healthnz-prm/fhir-engine-renderer'
import {
  CodeSystemLookup,
  Questionnaire,
  QuestionnaireResponse,
  ResponseStatus,
  ValueSetExpander
} from '@healthnz-prm/fhir-engine-renderer/types'
import { useCallback } from 'react'
import * as consumerComponents from '@/components/fhirEngine'
import * as clinicalComponents from '@/components/fhirEngineClinical'
import { expandValueSet } from '@/lib/expandValueSet'
import { lookupCodeSystem } from '@/lib/lookupCodeSystem'
import { getTheme } from '@/lib/theme'
import { submitQuestionnaire } from '@/lib/getQuestionnaire/submitQuestionnaire'
import { usePartialSave } from '@/hooks/usePartialSave'
import { usePreviousValues } from '@/hooks/usePreviousValues'
import { GenericError } from '@/lib/error'

export interface QuestionnaireWrapperData {
  questionnaire: Questionnaire
  questionnaireId: string
  authToken: string
}

export interface QuestionnaireWrapperProps {
  data: QuestionnaireWrapperData
  onError: (error: unknown) => void
  onPartialSave?: (partialSave: PartialSave) => void
  onSubmit?: (response: QuestionnaireResponse) => void
  Renderer?: typeof FHIREngineRenderer
  approvalWorkflow?: boolean
}

export function QuestionnaireWrapper({
  data,
  onError,
  onPartialSave,
  onSubmit,
  Renderer = FHIREngineRenderer,
  approvalWorkflow
}: QuestionnaireWrapperProps) {
  const { questionnaire, questionnaireId, authToken } = data

  const customComponents =
    getTheme() === 'clinical' ? clinicalComponents : consumerComponents

  const {
    clearPartialSaveData,
    data: partialSaveData,
    setPartialSaveData
  } = usePartialSave(questionnaireId)

  const { data: previousValues, savePreviousValue } =
    usePreviousValues(questionnaireId)

  // the renderer keys its option-fetching and terminology lookups off these functions'
  // identities, so inline arrows would re-request every dropdown/lookup on every render
  const expander = useCallback<ValueSetExpander>(
    (canonical, options) =>
      expandValueSet(canonical, options, { questionnaireId, authToken }),
    [questionnaireId, authToken]
  )

  const codeSystemLookup = useCallback<CodeSystemLookup>(
    (system, params) =>
      lookupCodeSystem(system, params, { questionnaireId, authToken }),
    [questionnaireId, authToken]
  )

  const handleSubmit = (response: QuestionnaireResponse) => {
    return submitQuestionnaire(response, { questionnaireId, authToken })
      .then(() => {
        clearPartialSaveData()
        onSubmit?.(response)
      })
      .catch(() =>
        onError(
          new GenericError(
            'An unexpected error occurred and we were unable to submit your response.',
            false
          )
        )
      )
  }

  const handlePartialSave = (partialSave: PartialSave) => {
    return setPartialSaveData(partialSave)
      .then(() => onPartialSave?.(partialSave))
      .catch(onError)
  }

  return (
    <Renderer
      customComponents={customComponents}
      isPartialSaveLoading={false}
      onPartialSave={handlePartialSave}
      onSubmit={handleSubmit}
      partialSaveData={partialSaveData}
      previousValues={previousValues}
      onSavePreviousValue={savePreviousValue}
      questionnaireData={questionnaire}
      expandValueSet={expander}
      lookupCodeSystem={codeSystemLookup}
      submitStatus={approvalWorkflow ? ResponseStatus.inProgress : undefined}
    />
  )
}
